import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from '../../hooks/useTranslation';
import useProductPurchase from '../../hooks/useProductPurchase';
import WidthWrapperEx from '../layout/WidthWrapperEx';
import ProductGallery from './ProductGallery';
import ProductStockBadge from './ProductStockBadge';
import ProductPurchase from './ProductPurchase';
import ProductInfo from './ProductInfo';
import ProductRelated from './ProductRelated';
import ProductReviews from './ProductReviews';
import { singleDetails } from '../singles/singlePresentation';
import HeartButton from '../wishlist/HeartButton';

export default function ProductDetail({ product, shipping, related = [], boughtTogether = [], breadcrumbs = [], drop = null, type = 'product', children }) {
  const { t } = useTranslation('product');
  const router = useRouter();
  const purchase = useProductPurchase(product, type);
  const isSingle = type === 'single';
  const details = isSingle ? singleDetails(product, t) : product;
  const category = isSingle ? { title: product.cardGame?.name || 'Singles', href: product.cardGame?.slug ? `/singles/${product.cardGame.slug}` : '/singles' }
    : (breadcrumbs || []).filter((crumb) => crumb.type === 'category').at(-1);
  const hasSpecs = details.attributes?.length > 0;
  const descriptionTitle = t(isSingle ? 'single.description' : 'info.description');
  const scrollToSection = async event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hash = event.currentTarget.hash;
    const section = document.getElementById(hash.slice(1));
    if (!section) return;
    event.preventDefault();
    const navigated = await router.push(hash, undefined, { shallow: true, scroll: false });
    if (navigated !== false) section.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  return <div className="bg-white font-['Montserrat',sans-serif] text-krit-ink selection:bg-krit-yellow selection:text-krit-ink">
    <WidthWrapperEx size="detail" fullHeight={false} className="px-4 py-3 sm:px-6 sm:py-6 lg:py-8">
      <section className="grid items-start gap-3 sm:gap-5 lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-x-10 lg:gap-y-5">
        <header className="min-w-0 lg:col-start-2 lg:row-start-1">
          <div className="mb-3 hidden flex-wrap items-center gap-3 sm:flex">
            {category ? <Link href={category.href || `/categorie/${category.slug}`} className="text-xs font-semibold text-krit-muted hover:text-krit-ink hover:underline">{category.title}</Link> : <span className="text-xs font-semibold text-krit-muted">KRIT / {t('hero.eyebrow')}</span>}
            <ProductStockBadge purchase={purchase} product={product} />
          </div>
          <div className="flex items-start justify-between gap-2">
            <h1 className="max-w-[26ch] break-words text-[20px] font-black leading-[1.2] tracking-[-0.035em] sm:text-[32px]">{product.title || product.name}</h1>
            <HeartButton itemType={isSingle ? 'card' : 'product'} itemId={product.id} variant="detail" className="-mr-1 -mt-2 shrink-0 sm:mt-0" />
          </div>
        </header>
        <div className="min-w-0 lg:col-start-1 lg:row-span-2 lg:row-start-1"><ProductGallery product={product} type={type} /></div>
        <div className="min-w-0 lg:col-start-2 lg:row-start-2"><ProductPurchase product={product} shipping={shipping} purchase={purchase} drop={drop} type={type} /></div>
      </section>
    </WidthWrapperEx>
    <nav aria-label={t('info.navigation')} className="bg-krit-paper">
      <WidthWrapperEx size="detail" fullHeight={false} className="flex flex-wrap items-center gap-x-7 gap-y-1 px-4 py-1 text-xs font-bold sm:gap-x-10 sm:px-6 sm:text-sm">
        <a href="#product-description" onClick={scrollToSection} className="inline-flex min-h-[44px] items-center hover:text-teal-700">{descriptionTitle}</a>
        {hasSpecs && <a href="#product-specs" onClick={scrollToSection} className="inline-flex min-h-[44px] items-center hover:text-teal-700">{t('info.details')}</a>}
        {product.reviews?.count > 0 && <a href="#product-reviews" onClick={scrollToSection} className="inline-flex min-h-[44px] items-center hover:text-teal-700">{t('reviews.title')} ({product.reviews.count})</a>}
        {boughtTogether?.length > 0 && <a href="#product-bought-together" onClick={scrollToSection} className="inline-flex min-h-[44px] items-center hover:text-teal-700">{t('boughtTogether.title')}</a>}
        {related?.length > 0 && <a href="#product-related" onClick={scrollToSection} className="inline-flex min-h-[44px] items-center hover:text-teal-700">{t('related.title')}</a>}
      </WidthWrapperEx>
    </nav>
    <WidthWrapperEx size="detail" fullHeight={false} className="px-4 py-8 sm:px-6 sm:py-10">
      <ProductInfo product={details} descriptionTitle={descriptionTitle} />
      <ProductReviews reviews={product.reviews} />
      <ProductRelated id="product-bought-together" title={t('boughtTogether.title')} items={boughtTogether || []} trackAs="bought_together_click" />
      <ProductRelated products={related || []} trackAs="related_click" />
      {children}
    </WidthWrapperEx>
  </div>;
}
